import { motion } from "framer-motion";
import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { TiMessages } from "react-icons/ti";
import GlassButton from "./ui/GlassButton";

const SupportTicketModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const logoRef = useRef(null);

  // Form states
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  const categories = [
    "Account Issues",
    "Wallet & Transactions",
    "ICO & Token Sale",
    "Technical Support",
    "Security Concerns",
    "Billing & Payments",
    "Feature Request",
    "Other"
  ];

  const handleSubmitForm = (e) => {
    e.preventDefault();
    console.log("Support Ticket Data:", {
      subject,
      category,
      priority,
      description,
      email,
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        modalRef.current,
        { scale: 0.8, opacity: 0, boxShadow: '0 0 0px rgba(64, 224, 208, 0)' },
        {
          scale: 1,
          opacity: 1,
          boxShadow: '0 0 30px rgba(64, 224, 208, 0.6)',
          duration: 0.8,
          ease: "power3.out"
        }
      );

      gsap.to(modalRef.current, {
        boxShadow: '0 0 30px rgba(64, 224, 208, 0.8)',
        repeat: -1,
        yoyo: true,
        duration: 2
      });

      gsap.fromTo(
        logoRef.current,
        { opacity: 0, scale: 0.8 },
        {
          opacity: 1,
          scale: 1,
          boxShadow: '0 0 15px rgba(64, 224, 208, 0.8)',
          duration: 0.8,
          ease: "power2.out"
        }
      );
    }
  }, [isOpen]);

  const handleClose = () => {
    gsap.to(modalRef.current, {
      scale: 0.1,
      opacity: 0,
      duration: 0.6,
      ease: "power3.inOut"
    });
    gsap.delayedCall(0.6, onClose);
  };

  if (!isOpen) return null;

  const inputBaseClasses = "w-full px-4 py-3 rounded-xl bg-black/40 backdrop-blur-sm border text-white placeholder-gray-500 transition-all duration-300 focus:outline-none";
  const inputFocusClasses = "border-cyan-500/50 shadow-[0_0_15px_rgba(0,255,255,0.3)]";
  const inputNormalClasses = "border-white/10 hover:border-white/20";
  const selectClasses = "w-full px-4 py-3 rounded-xl bg-black/40 backdrop-blur-sm border text-white transition-all duration-300 focus:outline-none cursor-pointer";

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-[9999] transition-opacity duration-300 p-4 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="relative bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md text-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto p-5 md:p-8 border border-cyan-500/20 scrollbar-thin scrollbar-thumb-cyan-500/30 scrollbar-track-transparent"
        ref={modalRef}
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0, 212, 170, 0.3) transparent' }}
      >
        {/* Close Button */}
        <motion.button
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border border-cyan-500/30 text-cyan-400 cursor-pointer transition-all duration-300"
          whileHover={{
            scale: 1.15,
            boxShadow: "0 0 20px rgba(0, 255, 255, 0.8), 0 0 40px rgba(0, 255, 255, 0.4)"
          }}
          whileTap={{ scale: 0.95 }}
          aria-label="Close modal"
        >
          <IoClose size={22} />
        </motion.button>

        {/* Logo */}
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 p-2 border border-cyan-500/30 rounded-xl shadow-lg shadow-cyan-500/10 bg-black/60 backdrop-blur-sm flex items-center justify-center">
            <div ref={logoRef} className="text-cyan-400">
              <TiMessages size={28} />
            </div>
          </div>
        </div>

        {/* Title */}
        <h2
          id="support-modal-title"
          className="text-2xl font-extrabold text-center mb-2 bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"
        >
          Open Support Ticket
        </h2>
        <p className="text-gray-400 text-center text-sm mb-5">
          Our team typically responds within 15 minutes
        </p>

        {/* Form */}
        <form onSubmit={handleSubmitForm} className="space-y-4">
          {/* Email */}
          <div className="relative group">
            <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-300">
              Email <span className="text-cyan-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              className={`${inputBaseClasses} ${focusedField === 'email' ? inputFocusClasses : inputNormalClasses}`}
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              required
            />
          </div>

          {/* Subject */}
          <div className="relative group">
            <label htmlFor="subject" className="block text-sm font-medium mb-2 text-gray-300">
              Subject <span className="text-cyan-400">*</span>
            </label>
            <input
              type="text"
              id="subject"
              className={`${inputBaseClasses} ${focusedField === 'subject' ? inputFocusClasses : inputNormalClasses}`}
              placeholder="Brief description of your issue"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setFocusedField('subject')}
              onBlur={() => setFocusedField(null)}
              required
            />
          </div>

          {/* Category & Priority Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div className="relative group">
              <label htmlFor="category" className="block text-sm font-medium mb-2 text-gray-300">
                Category <span className="text-cyan-400">*</span>
              </label>
              <select
                id="category"
                className={`${selectClasses} ${focusedField === 'category' ? inputFocusClasses : inputNormalClasses}`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                onFocus={() => setFocusedField('category')}
                onBlur={() => setFocusedField(null)}
                required
              >
                <option value="" className="bg-gray-900">Select category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat} className="bg-gray-900">{cat}</option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div className="relative group">
              <label htmlFor="priority" className="block text-sm font-medium mb-2 text-gray-300">
                Priority
              </label>
              <select
                id="priority"
                className={`${selectClasses} ${focusedField === 'priority' ? inputFocusClasses : inputNormalClasses}`}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                onFocus={() => setFocusedField('priority')}
                onBlur={() => setFocusedField(null)}
              >
                <option value="low" className="bg-gray-900">Low</option>
                <option value="medium" className="bg-gray-900">Medium</option>
                <option value="high" className="bg-gray-900">High</option>
                <option value="urgent" className="bg-gray-900">Urgent</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="relative group">
            <label htmlFor="description" className="block text-sm font-medium mb-2 text-gray-300">
              Description <span className="text-cyan-400">*</span>
            </label>
            <textarea
              id="description"
              rows="3"
              className={`${inputBaseClasses} resize-none ${focusedField === 'description' ? inputFocusClasses : inputNormalClasses}`}
              placeholder="Please describe your issue in detail..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
              required
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-3">
            <GlassButton
              type="submit"
              className="px-8 py-3"
              variant="cyan"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <TiMessages className="text-xl" /> Submit Ticket
              </div>
            </GlassButton>

            <GlassButton
              onClick={handleClose}
              className="px-8 py-3"
              variant="gray"
              size="lg"
            >
              <div className="flex items-center gap-2">
                <IoClose className="text-xl" /> Cancel
              </div>
            </GlassButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportTicketModal;
